import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

/**
 * Column preferences — visibility, order, pinning and density — kept in
 * component state and mirrored to `localStorage` so a user's table
 * layout survives reloads. Keyed by a stable `tableId` per table.
 *
 * The base `columns` array is the source of truth for what each column
 * *is* (header, render, sortable…). This hook layers the user's saved
 * *arrangement* on top: which columns are on, in what order, which are
 * frozen. New columns added to the code later appear automatically
 * (they're absent from the saved order, so they're appended).
 */

const PREFIX = 'fd.table.'

function loadPrefs(tableId) {
  if (!tableId) return {}
  try {
    return JSON.parse(localStorage.getItem(PREFIX + tableId) || '{}') || {}
  } catch {
    return {}
  }
}

function savePrefs(tableId, prefs) {
  if (!tableId) return
  try {
    localStorage.setItem(PREFIX + tableId, JSON.stringify(prefs))
  } catch {
    /* private mode / quota — non-fatal, prefs just won't persist */
  }
}

export const DENSITIES = ['compact', 'comfortable', 'relaxed']

/** Row vertical padding per density — applied to every body/header cell. */
export const DENSITY_PAD = {
  compact: 'py-1.5',
  comfortable: 'py-3',
  relaxed: 'py-4',
}

/**
 * The row height a table was EXPLICITLY given from its own density menu, or
 * null when it should follow the person's account default.
 *
 * Saves from before the account preference wrote a density on every render,
 * so a stored value alone doesn't prove a choice. A stored Compact or Relaxed
 * can only have come from the menu, so it is kept; a stored Comfortable was
 * the old default and is treated as "not chosen". From now on a menu pick is
 * saved with `densityExplicit: true`, which is always honoured.
 */
export function explicitDensityFrom(saved) {
  if (!saved || !DENSITIES.includes(saved.density)) return null
  if (saved.densityExplicit === true) return saved.density
  return saved.density === 'comfortable' ? null : saved.density
}

export function useTableColumns(tableId, columns, options = {}) {
  // The account's row-height preference (passed in by DataTable) — used
  // wherever this table has no explicit choice of its own.
  const defaultDensity = DENSITIES.includes(options.defaultDensity)
    ? options.defaultDensity
    : 'comfortable'

  // Hydrate once from storage; fall back to the code-declared defaults.
  // `density` holds only an EXPLICIT per-table choice (null = follow default).
  const [prefs, setPrefs] = useState(() => {
    const saved = loadPrefs(tableId)
    return {
      order: Array.isArray(saved.order) ? saved.order : null,
      hidden: Array.isArray(saved.hidden) ? saved.hidden : null,
      pinned: Array.isArray(saved.pinned) ? saved.pinned : null,
      density: explicitDensityFrom(saved),
    }
  })

  useEffect(() => {
    savePrefs(tableId, { ...prefs, densityExplicit: prefs.density != null })
  }, [tableId, prefs])

  // Resolve the effective per-column flags. `order` orders ALL known
  // columns; unknown/removed keys are dropped, brand-new keys appended
  // in their code order so the config stays authoritative.
  const arranged = useMemo(() => {
    const byKey = new Map(columns.map((c) => [c.key, c]))
    const savedOrder = prefs.order || []
    const ordered = []
    for (const k of savedOrder) if (byKey.has(k)) ordered.push(byKey.get(k))
    for (const c of columns) if (!savedOrder.includes(c.key)) ordered.push(c)

    const hiddenSet = new Set(
      prefs.hidden ?? columns.filter((c) => c.defaultHidden).map((c) => c.key),
    )
    const pinnedSet = new Set(
      prefs.pinned ?? columns.filter((c) => c.defaultPinned).map((c) => c.key),
    )

    return ordered.map((c) => ({
      ...c,
      hideable: c.hideable !== false,
      pinnable: !!c.pinnable,
      on: !hiddenSet.has(c.key),
      pinned: !!c.pinnable && pinnedSet.has(c.key),
    }))
  }, [columns, prefs.order, prefs.hidden, prefs.pinned])

  // Render order: pinned columns first (in their arranged order), then
  // the rest — mirrors the frozen-left layout of the table standard.
  const visibleColumns = useMemo(() => {
    const on = arranged.filter((c) => c.on)
    return [...on.filter((c) => c.pinned), ...on.filter((c) => !c.pinned)]
  }, [arranged])

  const setOrder = useCallback(
    (keysInOrder) => setPrefs((p) => ({ ...p, order: keysInOrder })),
    [],
  )
  const setHidden = useCallback(
    (hiddenKeys) => setPrefs((p) => ({ ...p, hidden: hiddenKeys })),
    [],
  )
  const togglePinned = useCallback(
    (key) =>
      setPrefs((p) => {
        const cur = new Set(
          p.pinned ?? columns.filter((c) => c.defaultPinned).map((c) => c.key),
        )
        cur.has(key) ? cur.delete(key) : cur.add(key)
        return { ...p, pinned: [...cur] }
      }),
    [columns],
  )
  const setDensity = useCallback(
    (density) => setPrefs((p) => ({ ...p, density })),
    [],
  )
  // Reset also drops an explicit row height, so the table goes back to
  // following the account default.
  const reset = useCallback(
    () =>
      setPrefs({
        order: null,
        hidden: null,
        pinned: null,
        density: null,
      }),
    [],
  )

  return {
    arranged, // every column + {on,pinned,hideable,pinnable} — for the manager
    visibleColumns, // pinned-first, on-only — for rendering
    density: prefs.density ?? defaultDensity,
    setOrder,
    setHidden,
    togglePinned,
    setDensity,
    reset,
  }
}

/**
 * Freeze-left layout. After each render, measures the width of every
 * pinned header cell (the leading control columns + any pinned data
 * columns) and writes matching `left` offsets onto the corresponding
 * body / foot cells, so the frozen columns stack correctly. Also
 * toggles scroll-shadow classes on the scroll container.
 *
 * Cells opt in with `data-pin-left` on the header, body and foot cells
 * of the same column index; the last frozen column also carries
 * `data-pin-edge` so the caller can paint the drop-shadow seam.
 *
 * @param {React.RefObject} scrollRef  the horizontally-scrollable div
 * @param {any[]} deps                 re-measure when these change
 */
export function useStickyPins(scrollRef, deps) {
  const rafRef = useRef(0)

  const syncShadows = useCallback(() => {
    const sc = scrollRef.current
    if (!sc) return
    // Left seam: shown once frozen columns scroll away from the edge.
    sc.classList.toggle('is-pinned-scrolled', sc.scrollLeft > 2)
    // Right seam: shown whenever there's more content to the right.
    sc.classList.toggle(
      'is-hscrollable',
      sc.scrollWidth - sc.clientWidth - sc.scrollLeft > 2,
    )
  }, [scrollRef])

  const measure = useCallback(() => {
    const sc = scrollRef.current
    if (!sc) return
    const headCells = sc.querySelectorAll('thead [data-pin-left]')
    if (headCells.length) {
      const lefts = []
      let off = 0
      headCells.forEach((th) => {
        lefts.push(off)
        off += th.getBoundingClientRect().width
      })
      const applyRow = (sel) =>
        sc.querySelectorAll(sel).forEach((tr) => {
          const cells = tr.querySelectorAll('[data-pin-left]')
          cells.forEach((c, i) => {
            if (lefts[i] != null) c.style.left = lefts[i] + 'px'
          })
        })
      headCells.forEach((th, i) => {
        th.style.left = lefts[i] + 'px'
      })
      applyRow('tbody tr')
      applyRow('tfoot tr')
    }
    syncShadows()
  }, [scrollRef, syncShadows])

  useLayoutEffect(() => {
    // rAF so the measure runs after the browser has laid the new DOM out.
    cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(measure)
    return () => cancelAnimationFrame(rafRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measure, ...(deps || [])])

  useEffect(() => {
    const sc = scrollRef.current
    if (!sc) return
    sc.addEventListener('scroll', syncShadows, { passive: true })
    const onResize = () => measure()
    window.addEventListener('resize', onResize)
    return () => {
      sc.removeEventListener('scroll', syncShadows)
      window.removeEventListener('resize', onResize)
    }
  }, [scrollRef, measure, syncShadows])

  return measure
}
